import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Trash2, ImagePlus, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable delete request dialog.
 * Props:
 *   isOpen, onClose, module, entryId, entryDisplay, entryDetails (string), onSuccess
 */
const DeleteRequestDialog = ({ isOpen, onClose, module, entryId, entryDisplay, entryDetails, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const imageRef = useRef(null);

  const reset = () => {
    setReason('');
    setImage(null);
    setImagePreview(null);
    if (imageRef.current) imageRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('module', module);
      fd.append('record_id', entryId);
      fd.append('record_display', entryDisplay || entryId);
      fd.append('reason', reason);
      if (image) fd.append('proof_image', image);
      await axios.post(`${API}/delete-requests`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Delete request submitted for approval', {
        description: 'An approver will review your request shortly',
        duration: 4000,
      });
      reset();
      onClose();
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" /> Request Record Deletion
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {(entryDisplay || entryDetails) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              {entryDisplay && <p className="font-semibold">{entryDisplay}</p>}
              {entryDetails && <p className="text-xs mt-0.5 text-amber-700">{entryDetails}</p>}
            </div>
          )}
          <div>
            <Label>Reason for Deletion <span className="text-red-500">*</span></Label>
            <Textarea
              className="mt-1.5 resize-none"
              rows={3}
              placeholder="Explain why this record should be deleted (e.g. duplicate, error, outdated)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="delete-reason-input"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              <ImagePlus className="h-4 w-4 text-slate-500" />
              Proof Image <span className="text-slate-400 text-xs ml-1">(Optional)</span>
            </Label>
            <div
              className="mt-1.5 border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-slate-400 transition-colors"
              onClick={() => imageRef.current?.click()}
            >
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="max-h-28 mx-auto rounded object-contain" />
                  <button type="button"
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                    onClick={(e) => { e.stopPropagation(); setImage(null); setImagePreview(null); if (imageRef.current) imageRef.current.value = ''; }}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div>
                  <ImagePlus className="h-7 w-7 mx-auto text-slate-300 mb-1" />
                  <p className="text-xs text-slate-500">Attach proof (photo, screenshot, paperwork)</p>
                </div>
              )}
            </div>
            <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
          </div>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={handleSubmit}
            disabled={submitting || !reason.trim()}
            data-testid="submit-delete-request-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteRequestDialog;
